import React from "react";
import { ADVERTISER, OVERVIEW, CHANNEL_PILL } from "../../../../../core/theme/reportTheme";
import { CHANNEL_NAMES, CHANNEL_TOOLTIPS } from "../../../../../core/data/marketOverviewText";
import { channelShownText, positionText, TOP3_LABEL } from "../../../../../core/data/advertiserOverviewText";
import { channelKeywordTotals } from "../../../../../core/data/advertiserGrid";

const dash = value => (value == null ? "–" : value);

// One channel (Ads, Maps, SEO, Shopping, Local Services Ads), in the stat-tile style:
// a tile filled with the channel colour, a coloured pill title, where the
// channel showed, one big number (0 when
// not seen), and #1 in that channel. The top 3 others are in the hover text
// and the expanded view (with the split by device and the counts per keyword).
// A channel nobody appeared in is a slim dashed tile.
// order: its place down Google's page (1, 2, 3...), shown top right like the
// Market Overview page layout tiles; none for channels nobody appeared in.
export default function ChannelCard({ channel, result, order, expandButton, expanded }) {
  const block = result.channels[channel];
  const of = result.coverage.searchesCompleted;
  const shown = block ? channelShownText(channel, block, of) : `none seen · 0 of ${of}`;
  const head = (
    <div className={ADVERTISER.cardHead}>
      <span className={CHANNEL_PILL[channel]}>{CHANNEL_NAMES[channel]}</span>
      <span className="flex items-center gap-1 shrink-0">
        {order && <span className={ADVERTISER.order} title={`${order} on Google's page`}>{order}</span>}
        {expandButton}
      </span>
    </div>
  );

  if (!block) {
    return (
      <div className={expanded ? ADVERTISER.cardExpanded : ADVERTISER.cardDashed}>
        {head}
        <p className={ADVERTISER.small}>{shown}</p>
      </div>
    );
  }

  const { you, leader, top3 } = block;
  const top3Text = top3 ? `${TOP3_LABEL}: ${top3.searches} searches · average position ${dash(top3.avgPosition)}` : "";
  const detail = [
    positionText(you),
    channel === "shopping" && you && you.withPrice > 0 ? `with a price in ${you.withPrice}` : "",
  ].filter(Boolean).join(" · ");

  // Lines: pill · big number "of 23" · your position · #1 · where the channel showed.
  return (
    <div className={expanded ? ADVERTISER.cardExpanded : ADVERTISER.channelCard[channel]} title={expanded ? undefined : top3Text}>
      {head}
      <div className={ADVERTISER.valueRow}>
        <span className={ADVERTISER.big}>{you ? you.searches : 0}</span>
        <span className={ADVERTISER.sub}>of {of} searches</span>
      </div>
      <p className={ADVERTISER.sub} title={detail}>{detail || "not seen"}</p>
      <p className={`flex min-w-0 gap-1 ${ADVERTISER.sub}`}>
        {leader
          ? <>
              <span className="truncate" title={leader.displayName}>#1 <span className="text-slate-900">{leader.displayName}</span></span>
              <span className="shrink-0">· {leader.searches} · avg {dash(leader.avgPosition)}</span>
            </>
          : "No other advertisers"}
      </p>
      <p className={ADVERTISER.small}>{shown}</p>

      {expanded && (
        <>
          {top3 && <p className="text-sm text-slate-600 mt-2">{top3Text}</p>}
          <ChannelDetail channel={channel} block={block} result={result} />
        </>
      )}
    </div>
  );
}

// A channel AdAudit doesn't search yet (AI Overview, Bing): the same slim
// tile as a channel nobody appeared in, with NOT SEARCHED.
export function NotSearchedTile({ channel }) {
  return (
    <div className={ADVERTISER.cardDashed} title={`${CHANNEL_NAMES[channel]}: not searched yet${CHANNEL_TOOLTIPS[channel] ? ` · ${CHANNEL_TOOLTIPS[channel]}` : ""}`}>
      <div className={ADVERTISER.cardHead}>
        <span className={CHANNEL_PILL[channel]}>{CHANNEL_NAMES[channel]}</span>
      </div>
      <p className={ADVERTISER.notSearched}>Not searched</p>
    </div>
  );
}

function ChannelDetail({ channel, block, result }) {
  const perKeyword = channelKeywordTotals(result, channel);
  const showDevices = result.sampling.devices.length > 0;
  return (
    <div className="mt-4 grid grid-cols-2 gap-6 text-sm">
      <div>
        <p className="font-semibold text-slate-900 mb-1">By device</p>
        {showDevices ? (
          <table className="w-full border-separate border-spacing-0">
            <thead><tr><th className={OVERVIEW.th} /><th className={OVERVIEW.th}>You</th>{block.leader && <th className={OVERVIEW.th}>#1 {block.leader.displayName}</th>}</tr></thead>
            <tbody>
              {["mobile", "desktop"].map(d => (
                <tr key={d}>
                  <td className={OVERVIEW.td}>{d === "mobile" ? "Mobile" : "Desktop"}</td>
                  <td className={OVERVIEW.td}>{block.byDevice[d].you.searches} of {block.byDevice[d].you.of}</td>
                  {block.leader && <td className={OVERVIEW.td}>{block.byDevice[d].leader.searches} of {block.byDevice[d].leader.of}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-slate-500">This snapshot didn't record devices.</p>}
      </div>
      <div>
        <p className="font-semibold text-slate-900 mb-1">By keyword (you)</p>
        {perKeyword.length === 0 ? (
          <p className="text-slate-500">Counts per keyword aren't available for {CHANNEL_NAMES[channel]}.</p>
        ) : (
          <table className="w-full border-separate border-spacing-0">
            <thead><tr><th className={OVERVIEW.th}>Keyword</th><th className={OVERVIEW.th}>Seen in</th></tr></thead>
            <tbody>
              {perKeyword.map(k => (
                <tr key={k.keyword}>
                  <td className={OVERVIEW.td}>{k.keyword}</td>
                  <td className={OVERVIEW.td}>{k.seenIn} of {k.searches} searches</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
